import React, { useState, useEffect } from 'react';
import { Usuario, PermissaoUsuario, Modulo, Acao } from '../../../../types/admin';
import { apiService } from '../../../../services/api';

interface Props {
  usuario: Usuario;
  onClose: () => void;
  onRefresh: () => void;
  initialTab?: 'lista' | 'adicionar';
}

export const ModalExcecoesUsuario: React.FC<Props> = ({ usuario, onClose, onRefresh, initialTab = 'lista' }) => {
  const [activeTab, setActiveTab] = useState<'lista' | 'adicionar'>(initialTab);
  const [excecoes, setExcecoes] = useState<PermissaoUsuario[]>([]);
  const [modulos, setModulos] = useState<Modulo[]>([]);
  const [acoes, setAcoes] = useState<Acao[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingExcecoes, setLoadingExcecoes] = useState(false);
  
  // Form state
  const [novaExcecao, setNovaExcecao] = useState({
    id_modulo: '',
    id_acao: '',
    tipo_permissao: 'ADICIONAR' as 'ADICIONAR' | 'REMOVER',
    permitido: true,
    motivo: ''
  });

  // Carregar exceções do usuário
  const fetchExcecoes = async () => {
    try {
      setLoadingExcecoes(true);
      const response = await apiService.getPermissoesUsuario({ id_usuario: usuario.id });
      setExcecoes(response.data);
    } catch (error) {
      console.error('Erro ao buscar exceções:', error);
    } finally {
      setLoadingExcecoes(false);
    }
  };

  // Carregar módulos
  const fetchModulos = async () => {
    try {
      const response = await apiService.getModulosSistema({});
      setModulos(response.data);
    } catch (error) {
      console.error('Erro ao buscar módulos:', error);
    }
  };

  // Carregar ações baseadas no módulo selecionado
  const fetchAcoes = async (idModulo?: string) => {
    try {
      const filtros = idModulo ? { id_modulo: parseInt(idModulo) } : {};
      const response = await apiService.getAcoesSistema(filtros);
      setAcoes(response.data);
    } catch (error) {
      console.error('Erro ao buscar ações:', error);
    }
  };

  // Adicionar exceção
  const handleAdicionarExcecao = async () => {
    if (!novaExcecao.id_modulo || !novaExcecao.id_acao) {
      window.alert('Por favor, selecione o módulo e a ação.');
      return;
    }

    try {
      setLoading(true);
      await apiService.createPermissaoUsuario({
        id_usuario: usuario.id,
        id_modulo: parseInt(novaExcecao.id_modulo),
        id_acao: parseInt(novaExcecao.id_acao),
        tipo_permissao: novaExcecao.tipo_permissao,
        permitido: novaExcecao.permitido,
        motivo: novaExcecao.motivo
      });

      window.alert('Exceção adicionada com sucesso!');
      setNovaExcecao({
        id_modulo: '',
        id_acao: '',
        tipo_permissao: 'ADICIONAR',
        permitido: true,
        motivo: ''
      });
      fetchExcecoes();
      setActiveTab('lista');
    } catch (error) {
      console.error('Erro ao adicionar exceção:', error);
      window.alert('Erro ao adicionar exceção. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Remover exceção
  const handleRemoverExcecao = async (excecaoId: number) => {
    if (!window.confirm('Tem certeza que deseja remover esta exceção?')) return;

    try {
      await apiService.deletePermissaoUsuario(excecaoId);
      fetchExcecoes();
      window.alert('Exceção removida com sucesso!');
    } catch (error) {
      console.error('Erro ao remover exceção:', error);
      window.alert('Erro ao remover exceção. Tente novamente.');
    }
  };

  useEffect(() => {
    fetchExcecoes();
    fetchModulos();
    fetchAcoes();
  }, [usuario.id]);

  // Atualizar ações quando módulo muda
  useEffect(() => {
    if (novaExcecao.id_modulo) {
      fetchAcoes(novaExcecao.id_modulo);
      setNovaExcecao(prev => ({ ...prev, id_acao: '' }));
    }
  }, [novaExcecao.id_modulo]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Nunca';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="modal fade" id="modal_excecoes_usuario" tabIndex={-1} role="dialog" aria-hidden="true">
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header border-bottom">
            <h5 className="modal-title">
              ⚠️ Exceções de Permissão - {usuario.nome} {usuario.sobrenome}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            >
              <i className="ti ti-x" />
            </button>
          </div>

          <div className="modal-body">
            {/* Navigation Tabs */}
            <ul className="nav nav-tabs mb-4">
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'lista' ? 'active' : ''}`}
                  onClick={() => setActiveTab('lista')}
                >
                  <i className="ti ti-list me-2"></i>
                  Exceções Ativas ({excecoes.length})
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'adicionar' ? 'active' : ''}`}
                  onClick={() => setActiveTab('adicionar')}
                >
                  <i className="ti-plus me-2"></i>
                  Adicionar Exceção
                </button>
              </li>
            </ul>

            {/* Tab Content */}
            {activeTab === 'lista' && (
              <div className="tab-pane active">
                {loadingExcecoes ? (
                  <div className="text-center py-4">
                    <div className="spinner-border" role="status"></div>
                    <p className="mt-2">Carregando exceções...</p>
                  </div>
                ) : excecoes.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>Tipo</th>
                          <th>Módulo</th>
                          <th>Ação</th>
                          <th>Permissão</th>
                          <th>Motivo</th>
                          <th>Data</th>
                          <th>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {excecoes.map(excecao => (
                          <tr key={excecao.id}>
                            <td>
                              <span className={`badge ${excecao.tipo_permissao === 'ADICIONAR' ? 'bg-success' : 'bg-danger'}`}>
                                {excecao.tipo_permissao === 'ADICIONAR' ? '✅ ADICIONAR' : '❌ REMOVER'}
                              </span>
                            </td>
                            <td>
                              <strong>{excecao.modulo_nome || excecao.modulo_codigo}</strong>
                            </td>
                            <td>
                              <span className="text-info">{excecao.acao_nome || excecao.acao_codigo}</span>
                            </td>
                            <td>
                              {excecao.permitido ? (
                                <span className="badge bg-success-light text-success">✓ Permitido</span>
                              ) : (
                                <span className="badge bg-danger-light text-danger">✗ Negado</span>
                              )}
                            </td>
                            <td>
                              <small className="text-muted">
                                {excecao.motivo || 'Sem motivo especificado'}
                              </small>
                            </td>
                            <td>
                              <small>{formatDate(excecao.data_criacao)}</small>
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleRemoverExcecao(excecao.id)}
                                title="Remover exceção"
                              >
                                <i className="ti ti-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <div className="avatar avatar-lg bg-success-light mx-auto mb-3">
                      <i className="ti ti-check-circle fs-2 text-success"></i>
                    </div>
                    <h6>Nenhuma exceção configurada</h6>
                    <p className="text-muted">
                      Este usuário segue exatamente as permissões do seu perfil base.
                    </p>
                    <button
                      className="btn btn-primary"
                      onClick={() => setActiveTab('adicionar')}
                    >
                      <i className="ti-plus me-1"></i>
                      Adicionar Primeira Exceção
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'adicionar' && (
              <div className="tab-pane active">
                <div className="row">
                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-header">
                        <h6 className="mb-0">➕ Nova Exceção</h6>
                      </div>
                      <div className="card-body">
                        <form onSubmit={(e) => { e.preventDefault(); handleAdicionarExcecao(); }}>
                          <div className="mb-3">
                            <label className="form-label">Tipo de Exceção</label>
                            <select
                              className="form-select"
                              value={novaExcecao.tipo_permissao}
                              onChange={(e) => setNovaExcecao(prev => ({ 
                                ...prev, 
                                tipo_permissao: e.target.value as 'ADICIONAR' | 'REMOVER' 
                              }))}
                            >
                              <option value="ADICIONAR">➕ Adicionar Permissão</option>
                              <option value="REMOVER">➖ Remover Permissão</option>
                            </select>
                            <small className="form-text text-muted">
                              <strong>Adicionar:</strong> Concede uma permissão que o perfil não possui<br />
                              <strong>Remover:</strong> Remove uma permissão que o perfil possui
                            </small>
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Módulo</label>
                            <select
                              className="form-select"
                              value={novaExcecao.id_modulo}
                              onChange={(e) => setNovaExcecao(prev => ({ ...prev, id_modulo: e.target.value }))}
                              required
                            >
                              <option value="">Selecione um módulo</option>
                              {modulos.map(modulo => (
                                <option key={modulo.id} value={modulo.id}>
                                  {modulo.nome} ({modulo.codigo})
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Ação</label>
                            <select
                              className="form-select"
                              value={novaExcecao.id_acao}
                              onChange={(e) => setNovaExcecao(prev => ({ ...prev, id_acao: e.target.value }))}
                              required
                              disabled={!novaExcecao.id_modulo}
                            >
                              <option value="">
                                {!novaExcecao.id_modulo ? 'Primeiro selecione um módulo' : 'Selecione uma ação'}
                              </option>
                              {acoes.map(acao => (
                                <option key={acao.id} value={acao.id}>
                                  {acao.nome} ({acao.codigo})
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Resultado da Exceção</label>
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="permitido"
                                id="permitido_sim"
                                checked={novaExcecao.permitido === true}
                                onChange={() => setNovaExcecao(prev => ({ ...prev, permitido: true }))}
                              />
                              <label className="form-check-label text-success" htmlFor="permitido_sim">
                                ✅ Permitir acesso
                              </label>
                            </div>
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="permitido"
                                id="permitido_nao"
                                checked={novaExcecao.permitido === false}
                                onChange={() => setNovaExcecao(prev => ({ ...prev, permitido: false }))}
                              />
                              <label className="form-check-label text-danger" htmlFor="permitido_nao">
                                ❌ Negar acesso
                              </label>
                            </div>
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Motivo da Exceção</label>
                            <textarea
                              className="form-control"
                              rows={3}
                              value={novaExcecao.motivo}
                              onChange={(e) => setNovaExcecao(prev => ({ ...prev, motivo: e.target.value }))}
                              placeholder="Digite o motivo desta exceção..."
                            />
                          </div>

                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                Adicionando...
                              </>
                            ) : (
                              <>
                                <i className="ti-plus me-2"></i>
                                Adicionar Exceção
                              </>
                            )}
                          </button>
                        </form>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-header">
                        <h6 className="mb-0">ℹ️ Como Funcionam as Exceções</h6>
                      </div>
                      <div className="card-body">
                        <div className="alert alert-info">
                          <h6>🎯 Tipos de Exceção:</h6>
                          <ul className="mb-0">
                            <li><strong>ADICIONAR:</strong> Concede uma permissão específica que o perfil base não possui</li>
                            <li><strong>REMOVER:</strong> Remove uma permissão específica que o perfil base possui</li>
                          </ul>
                        </div>

                        <div className="alert alert-warning">
                          <h6>⚠️ Importante:</h6>
                          <ul className="mb-0">
                            <li>As exceções são aplicadas <strong>após</strong> as permissões do perfil</li>
                            <li>Uma exceção <strong>sempre sobrescreve</strong> a permissão do perfil</li>
                            <li>Use exceções com moderação para manter a organização</li>
                          </ul>
                        </div>

                        <div className="alert alert-success">
                          <h6>✅ Exemplo Prático:</h6>
                          <p className="mb-2">
                            <strong>Perfil:</strong> "Editor" (pode editar, não pode excluir)<br />
                            <strong>Exceção:</strong> ADICIONAR → Excluir Produtos → Permitido
                          </p>
                          <p className="mb-0">
                            <strong>Resultado:</strong> Este usuário poderá excluir produtos, mesmo que o perfil "Editor" não permita.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Fechar
            </button>
            {activeTab === 'lista' && excecoes.length > 0 && (
              <button
                type="button"
                className="btn btn-outline-warning"
                onClick={() => setActiveTab('adicionar')}
              >
                <i className="ti-plus me-1"></i>
                Adicionar Nova Exceção
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};